import React, { useState, useCallback, useEffect } from 'react';
import { Form } from 'antd';
import srv from '../../../services';
import { ContentWrapper } from '../../_common';
import { useQueryClient, useQuery, useMutation } from 'react-query';
import {
 BaseForm,
 Content,
 SelectBlock,
 Address,
 Spin,
} from '../MeteringDevices.styled';
import ModalWarning from './ModalWarning';
import ModalConfirm from './ModalConfirm';
import SelectContract from '../../_common/SelectContract';
import SelectConnection from '../../_common/SelectConnection';
import TableMetering from './TableMetering';
import Snackbar from '@mui/material/Snackbar';
import { Alert, AlertColor } from '@mui/material';

export default function MeteringDevices() {
 const [snackBarOpened, setSnackBarOpened] = useState(false);
 const [snackBarType] = useState<AlertColor>('error');

 const {
  changeContract,
  changeConnection,
  handleCancel,
  handleOk,
  onConfirm,
  meterings,
  meteringDevice,
  contractNumber,
  contracts,
  isLoading,
  connections,
  isSubmiting,
  error,
  form,
  status,
  isModalVisible,
  value,
  statusConfirm,
  confirmError,
  data,
  ...formProps
 } = useForm();

 const connectId = connections
  ?.filter((connect) => {
   if (connect.id === meteringDevice) {
    return connect;
   }
   return null;
  })
  .find((item) => item);

 const checkMeteringDevice =
  meteringDevice !== null && meteringDevice !== undefined;

 const handleSnackBarClose = () => {
  setSnackBarOpened(false);
  sessionStorage.setItem('snackBarOpened', 'false');
  sessionStorage.setItem('snackBarMessage', '');
 };

 return (
  <React.Fragment>
   <ContentWrapper>
    <Content>
     <SelectBlock>
      <SelectContract
       changeContract={changeContract}
       contracts={contracts}
       value={contractNumber}
      />
      {contractNumber !== null && contractNumber !== undefined ? (
       <SelectConnection
        changeConnection={changeConnection}
        connections={connections}
        value={meteringDevice}
       />
      ) : null}
     </SelectBlock>
     {checkMeteringDevice ? (
      <Address>Адрес объекта: {connectId.address}</Address>
     ) : null}
    </Content>
    {isLoading ? (
     <Spin size="large" />
    ) : meteringDevice === null ? (
     ''
    ) : meterings === null ||
      meterings === undefined ||
      meterings?.length === 0 ? (
     'По данному договору приборы учета отсутствуют'
    ) : (
     <BaseForm layout="vertical" size="large" {...formProps} form={form}>
      {checkMeteringDevice ? (
       <>
        <TableMetering
         connect={connectId}
         meterings={meterings}
         isLoading={isLoading}
         isSubmiting={isSubmiting}
         form={form}
        />
       </>
      ) : null}
     </BaseForm>
    )}
    {status === 'loading' ? null : status === 'error' ? (
     <ModalWarning
      show={isModalVisible}
      meterings={meterings}
      isCancel={handleCancel}
      error={error}
      connectName={connectId?.name}
      periodStart={connectId?.inputPeriodStart}
      periodEnd={connectId?.inputPeriodStop}
     />
    ) : status === 'success' ? (
     <ModalConfirm
      show={isModalVisible}
      isCancel={handleCancel}
      confirm={onConfirm}
      connectName={connectId?.name}
      statusConfirm={statusConfirm}
      confirmError={confirmError}
      data={data}
     />
    ) : null}
   </ContentWrapper>

   <Snackbar
    open={Boolean(sessionStorage.getItem('snackBarOpened')) || snackBarOpened}
    autoHideDuration={2000}
    onClose={handleSnackBarClose}
    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
   >
    <Alert
     onClose={handleSnackBarClose}
     severity={snackBarType}
     sx={{ width: '100%' }}
    >
     {sessionStorage.getItem('snackBarMessage')}
    </Alert>
   </Snackbar>
  </React.Fragment>
 );
}

function useForm() {
 const [form] = Form.useForm();
 const contracts =
  useQueryClient().getQueryData<Models.Contract[]>('contracts');

 sessionStorage.setItem(
  'meteringForm',
  JSON.stringify(form.getFieldsValue(true))
 );
 const value = JSON.parse(sessionStorage.getItem('meteringForm'));

 const [contractNumber, setContractId] = useState<number>(null);
 const [meteringDevice, setMeteringDevice] = useState<number>(null);
 const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

 const changeContract = (value) => {
  setContractId(value);
  if (setContractId) {
   setMeteringDevice(null);
  }
 };

 const changeConnection = (value) => {
  setMeteringDevice(value);
 };

 const showModal = () => {
  setIsModalVisible(true);
 };

 const handleOk = () => {
  setTimeout(() => {
   setIsModalVisible(false);
  }, 3500);
 };

 const handleCancel = () => {
  setIsModalVisible(false);
 };

 const { data: connections } = useQuery<Models.Connection[]>(
  ['connections', contractNumber],
  () => srv.getConnections(contractNumber)
 );

 const { data: meterings, isLoading } = useQuery<Models.MeteringDevices[]>(
  ['meterings', meteringDevice],
  () => srv.getMeteringDevice(meteringDevice)
 );

 const {
  data,
  mutate: submit,
  isLoading: isSubmiting,
  status,
  error,
  reset,
 } = useMutation(srv.checkVolumeMetering);

 const onFinish = useCallback(
  (values) => {
   let errorFound = false;

   submit(
    meterings
     ?.filter((metering, index) => {
      if (
       values[`volume-${index}`] !== null &&
       values[`volume-${index}`] !== '' &&
       values[`volume-${index}`] !== undefined
      ) {
       return metering;
      }
      return null;
     })
     .map((filterArray) => {
      const index = meterings?.indexOf(filterArray);

      const checkVolume = Number(values[`volume-${index}`]);
      const checkMetering = (meterings[index] as any).controlScaleReadings;

      if (checkVolume < checkMetering) {
       errorFound = true;

       sessionStorage.setItem('snackBarOpened', 'true');

       sessionStorage.setItem(
        'snackBarMessage',
        'Введённые вами показания меньше контрольных показаний – ' +
         checkVolume +
         ' от ' +
         checkMetering +
         '. Пожалуйста уточните введённые показания.'
       );

       return null;
      }

      return {
       scaleId: filterArray.scaleId,
       volume: Number(values[`volume-${index}`]),
       meteringDeviceId: filterArray.meteringDeviceId,
       energyType: filterArray.energyType,
       contractId: filterArray.contractId,
       zeroCrossing: false,
       connectionId: filterArray.connectionId,
      };
     })
   );

   if (!errorFound) {
    showModal();
   }
  },
  [submit, meterings]
 );

 const onValuesChange = useCallback(() => {
  if (error) reset();
 }, [error, reset]);

 const {
  mutate: confirm,
  isLoading: isConfirm,
  error: confirmError,
  status: statusConfirm,
 } = useMutation(srv.sendVolumeMetering);

 const onConfirm = useCallback(() => {
  confirm(
   meterings
    ?.filter((metering, index) => {
     if (
      value[`volume-${index}`] !== null &&
      value[`volume-${index}`] !== '' &&
      value[`volume-${index}`] !== undefined
     ) {
      return metering;
     }
     return null;
    })
    .map((filterArray) => {
     const index = meterings?.indexOf(filterArray);
     return {
      scaleId: filterArray.scaleId,
      volume: Number(value[`volume-${index}`]),
      meteringDeviceId: filterArray.meteringDeviceId,
      energyType: filterArray.energyType,
      contractId: filterArray.contractId,
      zeroCrossing: false,
      connectionId: filterArray.connectionId,
     };
    })
  );
  handleOk();
 }, [confirm, meterings, value]);
 return {
  data,
  value,
  contractNumber,
  meteringDevice,
  isSubmiting,
  contracts,
  error,
  form,
  status,
  meterings,
  isLoading,
  connections,
  isModalVisible,
  isConfirm,
  statusConfirm,
  confirmError,
  onConfirm,
  onFinish,
  onValuesChange,
  handleCancel,
  handleOk,
  changeContract,
  changeConnection,
 };
}
